import Link from 'next/link';
import { trackEvent } from '@/lib/analytics';
import Icon from'@/components/ui/AppIcon';
import AppImage from'@/components/ui/AppImage';

interface ProductCardProps {
  product: {
    id: string
    name: string
    category: string
    description: string
    image: string
    alt: string
    specifications: {
      purity?: string
      packaging: string
      minimumOrder: string
    }
  }
}

export default function ProductCard({ product }: ProductCardProps) {
  const handleGetQuote = () => {
    trackEvent('product_interest', {
      product_id: product.id,
      product_name: product.name,
      product_category: product.category,
      action: 'get_quote',
    });
  };

  const handleViewDetails = () => {
    trackEvent('product_interest', {
      product_id: product.id,
      product_name: product.name,
      product_category: product.category,
      action: 'view_details',
    });
  };

  return (
    <div className="group bg-card rounded-organic overflow-hidden shadow-sm hover-lift h-full">
      {/* Image */}
      <div className="relative h-64 overflow-hidden">
        <AppImage
          src={product.image}
          alt={product.alt}
          className="w-full h-full object-cover image-scale"
        />
        
        {/* Category Badge */}
        <div className="absolute top-4 left-4 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider bg-white/95 backdrop-blur-sm text-primary shadow-md">
          {product.category}
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <h3 className="text-xl font-bold font-serif text-foreground mb-2 group-hover:text-primary transition-colors">
          {product.name}
        </h3>

        <p className="text-sm text-muted-foreground mb-4 leading-relaxed line-clamp-2">
          {product.description}
        </p>

        {/* Specifications */}
        <div className="space-y-2 mb-6">
          {product.specifications.purity && (
            <div className="flex items-start gap-2 text-xs">
              <Icon name="BeakerIcon" size={14} className="text-primary mt-0.5 flex-shrink-0" />
              <span className="text-muted-foreground">
                <span className="font-semibold text-foreground">Purity:</span> {product.specifications.purity}
              </span>
            </div>
          )}
          <div className="flex items-start gap-2 text-xs">
            <Icon name="CubeIcon" size={14} className="text-primary mt-0.5 flex-shrink-0" />
            <span className="text-muted-foreground">
              <span className="font-semibold text-foreground">Packaging:</span> {product.specifications.packaging}
            </span>
          </div>
          <div className="flex items-start gap-2 text-xs">
            <Icon name="ShoppingCartIcon" size={14} className="text-primary mt-0.5 flex-shrink-0" />
            <span className="text-muted-foreground">
              <span className="font-semibold text-foreground">Min. Order:</span> {product.specifications.minimumOrder}
            </span>
          </div>
        </div>

        {/* CTA Buttons */}
        <div className="flex gap-3">
          <Link
            href="/contact"
            onClick={handleGetQuote}
            className="flex-1 inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground px-4 py-2.5 rounded-full text-sm font-semibold hover:bg-primary/90 transition-colors"
          >
            Get Quote
            <Icon name="ArrowUpRightIcon" size={14} />
          </Link>
          <button 
            onClick={handleViewDetails}
            className="px-4 py-2.5 rounded-full border border-border hover:border-primary hover:bg-primary/5 transition-colors"
          >
            <Icon name="InformationCircleIcon" size={20} className="text-primary" />
          </button>
        </div>
      </div>
    </div>
  )
}